/**
 * Shortcode Slider Initializers
 *
 * This module provides initialization functions for Swiper sliders used in shortcodes.
 * Features include:
 * - Error handling and validation
 * - Responsive breakpoints
 * - Accessibility improvements
 * - Performance optimizations
 * - Cleanup/destroy methods
 */

import Swiper from 'swiper';
import {Autoplay, Pagination, Navigation, Keyboard, A11y} from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

// Store slider instances for cleanup
const sliderInstances = new Map();

/**
 * Utility function to safely initialize a slider
 * @param {HTMLElement} sliderElement - The slider container element
 * @param {Object} config - Swiper configuration object
 * @param {string} instanceKey - Unique key for storing the instance
 * @returns {Swiper|null} - The Swiper instance or null if initialization failed
 */
function safeInitSlider(sliderElement, config, instanceKey) {
    try {
        // Validate element exists
        if (!sliderElement || !(sliderElement instanceof HTMLElement)) {
            console.warn(`[Slider Init] Invalid element provided for ${instanceKey}`);
            return null;
        }

        // Check if already initialized
        if (sliderElement.swiper) {
            console.debug(`[Slider Init] Slider ${instanceKey} already initialized, skipping...`);
            return sliderElement.swiper;
        }

        // Check if slider has slides
        const slides = sliderElement.querySelectorAll('.swiper-slide');
        if (!slides || slides.length === 0) {
            console.warn(`[Slider Init] No slides found for ${instanceKey}`);
            return null;
        }

        // Merge default config with provided config
        const defaultConfig = {
            modules: [Pagination, Autoplay, Keyboard, A11y],
            watchOverflow: true,
            observer: true,
            observeParents: true,
            a11y: {
                enabled: true,
                prevSlideMessage: 'Previous slide',
                nextSlideMessage: 'Next slide',
                firstSlideMessage: 'This is the first slide',
                lastSlideMessage: 'This is the last slide',
                paginationBulletMessage: 'Go to slide {{index}}',
            },
            keyboard: {
                enabled: true,
                onlyInViewport: true,
            },
        };

        const finalConfig = {
            ...defaultConfig,
            ...config,
            // Merge nested objects
            pagination: {
                ...defaultConfig.pagination,
                ...(config.pagination || {}),
            },
            autoplay: {
                ...defaultConfig.autoplay,
                ...(config.autoplay || {}),
            },
        };

        // Initialize Swiper
        const swiperInstance = new Swiper(sliderElement, finalConfig);

        // Store instance for cleanup
        sliderInstances.set(instanceKey, swiperInstance);

        console.debug(`[Slider Init] Successfully initialized ${instanceKey}`);
        return swiperInstance;
    } catch (error) {
        console.error(`[Slider Init] Error initializing ${instanceKey}:`, error);
        return null;
    }
}

/**
 * Initialize testimonials slider
 * Supports responsive breakpoints and accessibility features
 */
export function initTestimonialsSlider() {
    const sliders = document.querySelectorAll('.testimonials-slider');

    if (!sliders || sliders.length === 0) {
        console.debug('[Testimonials Slider] No sliders found');
        return;
    }

    sliders.forEach((slider, index) => {
        const instanceKey = `testimonials-${index}`;
        const paginationEl = slider.parentElement?.querySelector('.swiper-pagination-testimonials');

        const config = {
            modules: [Pagination, Autoplay, Keyboard, A11y],
            slidesPerView: 'auto',
            spaceBetween: 30,
            loop: true,
            centeredSlides: false,
            grabCursor: true,
            pagination: {
                el: paginationEl || null,
                clickable: true,
                dynamicBullets: true,
                dynamicMainBullets: 3,
            },
            autoplay: {
                delay: 5000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
            },
            breakpoints: {
                320: {
                    slidesPerView: 1,
                    spaceBetween: 20,
                },
                640: {
                    slidesPerView: 'auto',
                    spaceBetween: 24,
                },
                768: {
                    slidesPerView: 'auto',
                    spaceBetween: 30,
                },
                1024: {
                    slidesPerView: 'auto',
                    spaceBetween: 30,
                },
            },
            // Performance optimizations
            speed: 600,
            preloadImages: false,
            lazy: {
                loadPrevNext: true,
                loadPrevNextAmount: 2,
            },
        };

        safeInitSlider(slider, config, instanceKey);
    });
}

/**
 * Initialize team members slider
 * Optimized for team member cards with responsive design
 */
export function initTeamMembersSlider() {
    const sliders = document.querySelectorAll('.team-members-slider');

    if (!sliders || sliders.length === 0) {
        console.debug('[Team Members Slider] No sliders found');
        return;
    }

    sliders.forEach((slider, index) => {
        const instanceKey = `team-members-${index}`;
        const paginationEl = slider.parentElement?.querySelector('.swiper-pagination-team-members');

        const config = {
            modules: [Pagination, Autoplay, Keyboard, A11y],
            slidesPerView: 'auto',
            spaceBetween: 0,
            loop: true,
            centeredSlides: false,
            grabCursor: true,
            pagination: {
                el: paginationEl || null,
                clickable: true,
                dynamicBullets: true,
                dynamicMainBullets: 3,
            },
            autoplay: {
                delay: 5500,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
            },
            breakpoints: {
                320: {
                    slidesPerView: 1,
                    spaceBetween: 16,
                },
                480: {
                    slidesPerView: 'auto',
                    spaceBetween: 20,
                },
                640: {
                    slidesPerView: 'auto',
                    spaceBetween: 24,
                },
                768: {
                    slidesPerView: 'auto',
                    spaceBetween: 0,
                },
                1024: {
                    slidesPerView: 'auto',
                    spaceBetween: 0,
                },
            },
            // Performance optimizations
            speed: 600,
            preloadImages: false,
            lazy: {
                loadPrevNext: true,
                loadPrevNextAmount: 2,
            },
        };

        safeInitSlider(slider, config, instanceKey);
    });
}

